import toast from "react-hot-toast";

/**
 * Standard branded styling for TechMicro Admin toasts
 */
const BASE_TOAST_STYLE = {
  borderRadius: "10px",
  fontSize: "14px",
  fontWeight: "500",
  padding: "12px 18px",
  boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
  color: "#1e293b",
};

/**
 * Success toast with standard duration and styling
 */
export const showSuccessToast = (message, options = {}) => {
  return toast.success(message, {
    duration: 4000,
    style: {
      ...BASE_TOAST_STYLE,
      border: "1px solid #bbf7d0",
      backgroundColor: "#ffffff",
    },
    iconTheme: {
      primary: "#16a34a",
      secondary: "#ffffff",
    },
    ...options,
  });
};

/**
 * Error toast with longer duration for readability
 */
export const showErrorToast = (message, options = {}) => {
  return toast.error(message || "An unexpected error occurred. Please try again.", {
    duration: 5000,
    style: {
      ...BASE_TOAST_STYLE,
      border: "1px solid #fecaca",
      backgroundColor: "#ffffff",
    },
    iconTheme: {
      primary: "#dc2626",
      secondary: "#ffffff",
    },
    ...options,
  });
};

/**
 * Informational toast
 */
export const showInfoToast = (message, options = {}) => {
  return toast(message, {
    duration: 4000,
    icon: "ℹ️",
    style: {
      ...BASE_TOAST_STYLE,
      border: "1px solid #bfdbfe",
      backgroundColor: "#ffffff",
    },
    ...options,
  });
};

/**
 * Promise-based toast for async operations
 */
export const showPromiseToast = (promise, messages = {}, options = {}) => {
  return toast.promise(
    promise,
    {
      loading: messages.loading || "Processing update...",
      success: messages.success || "Updated successfully!",
      error: (err) => err?.message || messages.error || "Update failed. Please try again.",
    },
    {
      style: BASE_TOAST_STYLE,
      success: {
        duration: 4000,
        style: {
          ...BASE_TOAST_STYLE,
          border: "1px solid #bbf7d0",
        },
      },
      error: {
        duration: 5000,
        style: {
          ...BASE_TOAST_STYLE,
          border: "1px solid #fecaca",
        },
      },
      ...options,
    }
  );
};

/**
 * Rich in-app notification toast (e.g., when a background/realtime event lands)
 */
export const showInAppNotificationToast = (
  { title, message, targetUrl, category },
  onNavigate
) => {
  return toast.custom(
    (t) => (
      <div
        className={`${
          t.visible ? "animate-enter" : "animate-leave"
        } max-w-md w-full bg-white shadow-lg rounded-3 pointer-events-auto flex ring-1 ring-black ring-opacity-5 border`}
        style={{
          minWidth: "320px",
          maxWidth: "420px",
          padding: "14px 16px",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          borderLeft: "4px solid var(--bg-primary-color, #00509d)",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15)",
        }}
      >
        <div className="d-flex align-items-start gap-3 w-100">
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              backgroundColor: "rgba(0, 80, 157, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              flexShrink: 0,
            }}
          >
            🔔
          </div>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            {title && (
              <div
                className="fw-bold text-dark mb-1"
                style={{ fontSize: "13px", lineHeight: "1.2" }}
              >
                {title}
              </div>
            )}
            <div
              className="text-secondary"
              style={{
                fontSize: "12px",
                lineHeight: "1.4",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {message}
            </div>
            {targetUrl && typeof onNavigate === "function" && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => {
                    toast.dismiss(t.id);
                    onNavigate(targetUrl);
                  }}
                  className="btn btn-sm btn-primary py-0 px-2 fw-semibold"
                  style={{
                    fontSize: "11px",
                    borderRadius: "6px",
                    backgroundColor: "var(--bg-primary-color, #00509d)",
                    borderColor: "var(--bg-primary-color, #00509d)",
                  }}
                >
                  View Details &rarr;
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => toast.dismiss(t.id)}
            className="btn-close btn-close-sm"
            style={{ fontSize: "10px", opacity: 0.6 }}
            aria-label="Close"
          />
        </div>
      </div>
    ),
    {
      duration: 6000,
      position: "top-right",
    }
  );
};

export default toast;
